import { OrganizationLogo } from "./organization-logo/organization-logo.model";
import { addMonths, format, startOfMonth } from "date-fns";
import { CrudAbstractModel } from "../crud-abstract/crud-abstract.model";
import { User } from "../user/user.model";
import { OrganizationTermsTranslation } from "./organization-terms/organization-terms-translation.model";
import { CarrierIntegrationMapper } from "./carrier-integration.mapper";
import {
  CarrierIntegration,
  CarrierIntegrationState,
  CarrierProvider,
  carrierProviders,
} from "./carrier-integration.model";

export class Organization extends CrudAbstractModel {
  private carrierIntegrations: Record<CarrierProvider, CarrierIntegration> =
    Object.fromEntries(
      carrierProviders.map((provider) => [
        provider,
        CarrierIntegrationMapper.map(null),
      ]),
    ) as Record<CarrierProvider, CarrierIntegration>;
  private monthlyFreeCredits = 0;
  private availableCredits = 0;

  constructor(
    public id: number,
    private name: string,
    private logo: OrganizationLogo,
    private numberOfLicenses: number,
    private createdAt: Date,
    private modifiedAt: Date,
    private activeTo: Date,
    private statisticsRefreshedAt: Date,
    private nip: string,
    private street: string,
    private postalCode: string,
    private city: string,
    private country: string,
    private emailAuthMode: string,
    private emailEncryption: string,
    private emailHost: string,
    private emailImapHost: string,
    private emailImapPort: number,
    private emailImapSsl: boolean,
    private emailPort: number,
    private spread: number,
    private quoteCollectingEmail: string,
    private allTerms: OrganizationTermsTranslation[],
    private currency: string,
    private insuranceValueMultiplierPercent: number,
    private insuranceRatePercent: number,
    private insuranceMinValue: number,
    private insuranceMinCurrency: string,
    private ecuAccountId: number,
    private showCarrier: boolean,
    private showRateSurcharges: boolean,
    private webCargoNetKey1: string | null,
    private webCargoNetKey2: string | null,
    private cmaBehalfOf: string | null,
    private bluexUserApiKey: string | null,
    private okargoToken: string | null,
    private okargoCarriers: Record<string, string> | null,
    private shipcoApiCustomerAlias: string | null,
    private bank: string | null,
    private dateFormat: string | null,
    private pdfLayout: boolean | null,
    private brandColor: string | null,
    private modulesCrm: boolean,
  ) {
    super(id);
  }

  public getNumberOfLicenses(): number {
    return this.numberOfLicenses;
  }
  public setNumberOfLicenses(param: number) {
    this.numberOfLicenses = param;
  }

  public getCreatedAt(): Date {
    return this.createdAt;
  }
  public setCreatedAt(param: Date) {
    this.createdAt = param;
  }

  public getModifiedAt(): Date {
    return this.modifiedAt;
  }
  public setModifiedAt(param: Date) {
    this.modifiedAt = param;
  }

  public getActiveTo(): Date {
    return this.activeTo;
  }
  public setActiveTo(param: Date) {
    this.activeTo = param;
  }

  public getStatisticsRefreshedAt(): Date {
    return this.statisticsRefreshedAt;
  }
  public setStatisticsRefreshedAt(param: Date) {
    this.statisticsRefreshedAt = param;
  }

  public getNip(): string {
    return this.nip;
  }
  public setNip(param: string) {
    this.nip = param;
  }

  public getStreet(): string {
    return this.street;
  }
  public setStreet(param: string) {
    this.street = param;
  }

  public getPostalCode(): string {
    return this.postalCode;
  }
  public setPostalCode(param: string) {
    this.postalCode = param;
  }

  public getCity(): string {
    return this.city;
  }
  public setCity(param: string) {
    this.city = param;
  }

  public getCountry(): string {
    return this.country;
  }
  public setCountry(param: string) {
    this.country = param;
  }

  public getEmailAuthMode(): string {
    return this.emailAuthMode;
  }
  public setEmailAuthMode(param: string) {
    this.emailAuthMode = param;
  }

  public getEmailEncryption(): string {
    return this.emailEncryption;
  }
  public setEmailEncryption(param: string) {
    this.emailEncryption = param;
  }

  public getEmailHost(): string {
    return this.emailHost;
  }
  public setEmailHost(param: string) {
    this.emailHost = param;
  }

  public getEmailImapHost(): string {
    return this.emailImapHost;
  }
  public setEmailImapHost(param: string) {
    this.emailImapHost = param;
  }

  public getEmailImapPort(): number {
    return this.emailImapPort;
  }
  public setEmailImapPort(param: number) {
    this.emailImapPort = param;
  }

  public getEmailImapSsl(): boolean {
    return this.emailImapSsl;
  }
  public setEmailImapSsl(param: boolean) {
    this.emailImapSsl = param;
  }

  public getEmailPort(): number {
    return this.emailPort;
  }
  public setEmailPort(param: number) {
    this.emailPort = param;
  }

  public getSpread(): number {
    return this.spread;
  }
  public setSpread(param: number) {
    this.spread = param;
  }

  public getQuoteCollectingEmail(): string {
    return this.quoteCollectingEmail;
  }
  public setQuoteCollectingEmail(param: string) {
    this.quoteCollectingEmail = param;
  }

  public getAllTerms(): OrganizationTermsTranslation[] {
    return this.allTerms;
  }
  public setAllTerms(terms: OrganizationTermsTranslation[]) {
    this.allTerms = terms;
  }

  public getId(): number {
    return this.id;
  }

  public getName(): string {
    return this.name;
  }

  public getLogo(): OrganizationLogo {
    return this.logo;
  }

  public getCurrency(): string {
    return this.currency;
  }
  public setCurrency(param: string) {
    this.currency = param;
  }

  public getInsuranceValueMultiplierPercent(): number {
    return this.insuranceValueMultiplierPercent;
  }
  public setInsuranceValueMultiplierPercent(param: number) {
    this.insuranceValueMultiplierPercent = param;
  }

  public getInsuranceRatePercent(): number {
    return this.insuranceRatePercent;
  }
  public setInsuranceRatePercent(param: number) {
    this.insuranceRatePercent = param;
  }

  public getInsuranceMinValue(): number {
    return this.insuranceMinValue;
  }
  public setInsuranceMinValue(param: number) {
    this.insuranceMinValue = param;
  }

  public getInsuranceMinCurrency(): string {
    return this.insuranceMinCurrency;
  }
  public setInsuranceMinCurrency(param: string) {
    this.insuranceMinCurrency = param;
  }

  public getEcuAccountId(): number {
    return this.ecuAccountId;
  }
  public setEcuAccountId(param: number) {
    this.ecuAccountId = param;
  }

  public getShowCarrier(): boolean {
    return this.showCarrier;
  }
  public setShowCarrier(param: boolean): void {
    this.showCarrier = param;
  }

  public getShowRateSurcharges(): boolean {
    return this.showRateSurcharges;
  }
  public setShowRateSurcharges(param: boolean): void {
    this.showRateSurcharges = param;
  }

  public getWebCargoNetKey1(): string | null {
    return this.webCargoNetKey1;
  }
  public setWebCargoNetKey1(param: string): void {
    this.webCargoNetKey1 = param;
  }

  public getWebCargoNetKey2(): string | null {
    return this.webCargoNetKey2;
  }
  public setWebCargoNetKey2(param: string): void {
    this.webCargoNetKey2 = param;
  }

  public getCmaBehalfOf(): string | null {
    return this.cmaBehalfOf;
  }
  public setCmaBehalfOf(param: string): void {
    this.cmaBehalfOf = param;
  }

  public getBluexUserApiKey(): string | null {
    return this.bluexUserApiKey;
  }

  public getCarrierIntegration(provider: CarrierProvider): CarrierIntegration {
    return this.carrierIntegrations[provider];
  }

  public applyCarrierIntegration(
    provider: CarrierProvider,
    state: CarrierIntegrationState,
  ): void {
    this.carrierIntegrations[provider] = new CarrierIntegration(state);
  }

  public setBluexUserApiKey(param: string): void {
    this.bluexUserApiKey = param;
  }

  public getOkargoToken(): string | null {
    return this.okargoToken;
  }
  public setOkargoToken(param: string): void {
    this.okargoToken = param;
  }

  public getOkargoCarriers(): Record<string, string> | null {
    return this.okargoCarriers;
  }

  public getShipcoApiCustomerAlias(): string | null {
    return this.shipcoApiCustomerAlias;
  }
  public setShipcoApiCustomerAlias(param: string): void {
    this.shipcoApiCustomerAlias = param;
  }

  public getBank(): string | null {
    return this.bank;
  }
  public setBank(param: string): void {
    this.bank = param;
  }

  public getDateFormat(): string | null {
    return this.dateFormat;
  }
  public setDateFormat(param: string): void {
    this.dateFormat = param;
  }

  public getPdfLayout(): boolean | null {
    return this.pdfLayout;
  }
  public setPdfLayout(param: boolean): void {
    this.pdfLayout = param;
  }

  public getBrandColor(): string | null {
    return this.brandColor;
  }
  public setBrandColor(param: string | null): void {
    this.brandColor = param;
  }

  public getModulesCrm(): boolean {
    return this.modulesCrm;
  }
  public setModulesCrm(param: boolean): void {
    this.modulesCrm = param;
  }

  public getMonthlyFreeCredits(): number {
    return this.monthlyFreeCredits;
  }

  public getAvailableCredits(): number {
    return this.availableCredits;
  }

  public applyCredits(monthlyFreeCredits: number, availableCredits: number) {
    this.monthlyFreeCredits = monthlyFreeCredits;
    this.availableCredits = availableCredits;
  }

  public nextCreditRenewal(today: Date = new Date()): Date {
    return startOfMonth(addMonths(today, 1));
  }

  public getRequestParams(user?: User) {
    return {
      name: this.getName(),
      logo:
        this.getLogo() instanceof OrganizationLogo
          ? this.getLogo().getPath()
          : this.getLogo(),
      numberOfLicenses: this.getNumberOfLicenses(),
      activeTo: format(this.getActiveTo(), "yyyy-MM-dd"),
      nip: this.getNip(),
      street: this.getStreet(),
      postalCode: this.getPostalCode(),
      city: this.getCity(),
      country: this.getCountry(),
      emailAuthMode: this.getEmailAuthMode(),
      emailEncryption: this.getEmailEncryption(),
      emailHost: this.getEmailHost(),
      emailImapHost: this.getEmailImapHost(),
      emailImapPort: this.getEmailImapPort(),
      emailImapSsl: this.getEmailImapSsl(),
      emailPort: this.getEmailPort(),
      spread: this.getSpread(),
      quoteCollectingEmail: this.getQuoteCollectingEmail(),
      allTerms: this.getAllTerms(),
      currency: this.getCurrency(),
      insuranceValueMultiplierPercent:
        this.getInsuranceValueMultiplierPercent(),
      insuranceRatePercent: this.getInsuranceRatePercent(),
      insuranceMinValue: this.getInsuranceMinValue(),
      insuranceMinCurrency: this.getInsuranceMinCurrency(),
      ecuAccountId: this.getEcuAccountId(),
      showCarrier: this.getShowCarrier(),
      showRateSurcharges: this.getShowRateSurcharges(),
      webCargoNetKey1: this.getWebCargoNetKey1(),
      webCargoNetKey2: this.getWebCargoNetKey2(),
      cmaBehalfOf: this.getCmaBehalfOf(),
      bluexUserApiKey: this.getBluexUserApiKey(),
      okargoToken: this.getOkargoToken(),
      shipcoApiCustomerAlias: this.getShipcoApiCustomerAlias(),
      bank: this.getBank(),
      dateFormat: this.getDateFormat(),
      pdfLayout: this.getPdfLayout(),
      brandColor: this.getBrandColor(),
      modulesCrm: this.getModulesCrm(),
    };
  }
}
